// src/admin/pages/Costs/ExtraRevenuesList.jsx

import React, { useEffect, useState } from "react";
import {
  fetchExtraRevenues,
  createExtraRevenue,
  updateExtraRevenue,
  deleteExtraRevenue,
} from "./costsApi";
import { useMediaQuery } from "react-responsive";

/**
 * ExtraRevenuesList – elenco ricavi extra con layout reattivo:
 * - Schermi piccoli (mobile): card layout singolo ricavo
 * - Schermi grandi (desktop): tabella
 * - Form di creazione con gestione di ricorrenti e date obbligatorie
 * - Inline editing con pulsanti Salva/Annulla
 * - Evita zoom su iOS impostando un font-size >=16px sugli input
 */

const palette = {
  primary: "#FF8C00",
  secondary: "#FF6600",
  background: "#FAFAFA",
  card: "#FFFFFF",
  textDark: "#333333",
  textLight: "#FFFFFF",
  border: "#DDDDDD",
};

// Stili ottimizzati per mobile, prevenendo zoom su iOS
const styles = {
  container: {
    padding: "16px 8px",
    backgroundColor: palette.background,
    boxSizing: "border-box",
    overflowX: "hidden", // niente scroll orizzontale
  },
  title: {
    fontSize: "18px",
    marginBottom: "16px",
    textAlign: "center",
    color: palette.textDark,
    fontWeight: "bold",
  },
  formCard: {
    backgroundColor: palette.card,
    padding: "16px",
    borderRadius: "6px",
    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
    marginBottom: "16px",
  },
  formTitle: {
    marginTop: 0,
    marginBottom: "12px",
    fontSize: "16px",
    textAlign: "center",
    color: palette.textDark,
    fontWeight: "bold",
  },
  formRow: {
    display: "flex",
    flexDirection: "column",
    marginBottom: "10px",
  },
  label: {
    marginBottom: "4px",
    fontSize: "15px", // leggermente maggiore
    color: palette.textDark,
  },
  input: {
    padding: "10px",
    border: `1px solid ${palette.border}`,
    borderRadius: "4px",
    fontSize: "16px", // >=16px per evitare zoom automatico su iOS
    boxSizing: "border-box",
  },
  select: {
    padding: "10px",
    border: `1px solid ${palette.border}`,
    borderRadius: "4px",
    fontSize: "16px", // >=16px
    boxSizing: "border-box",
  },
  addButton: {
    backgroundColor: palette.primary,
    color: palette.textLight,
    border: "none",
    borderRadius: "4px",
    padding: "12px 16px",
    cursor: "pointer",
    fontSize: "16px",
    fontWeight: "bold",
    marginTop: "6px",
    width: "100%",
  },
  // Card layout per mobile
  card: {
    backgroundColor: palette.card,
    marginBottom: 16,
    padding: 16,
    borderRadius: 6,
    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
  },
  // Tabella per desktop
  table: {
    width: "100%",
    borderCollapse: "collapse",
    backgroundColor: palette.card,
    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
    marginTop: "16px",
    borderRadius: "6px",
    overflow: "hidden",
  },
  th: {
    backgroundColor: "#336699",
    color: palette.textLight,
    textAlign: "left",
    padding: "10px",
    fontSize: "15px",
  },
  td: {
    padding: "10px",
    fontSize: "15px",
    color: palette.textDark,
    borderBottom: `1px solid ${palette.border}`,
    verticalAlign: "middle",
  },
  editInput: {
    marginBottom: "8px",
    display: "block",
    padding: "10px",
    border: `1px solid ${palette.border}`,
    borderRadius: "4px",
    fontSize: "16px", // >=16px
    boxSizing: "border-box",
  },
  actionButton: {
    backgroundColor: palette.secondary,
    color: palette.textLight,
    border: "none",
    borderRadius: "4px",
    padding: "10px 14px",
    cursor: "pointer",
    fontSize: "15px",
    fontWeight: "bold",
  },
  cancelButton: {
    backgroundColor: "#999",
    marginLeft: 8,
  },
  mobileFieldLabel: {
    fontWeight: "bold",
    marginTop: "6px",
    display: "block",
    fontSize: "15px",
  },
};

export default function ExtraRevenuesList({ restaurantId }) {
  const [revenues, setRevenues] = useState([]);
  const [loading, setLoading] = useState(false);

  // Campi creazione
  const [revName, setRevName] = useState("");
  const [revAmount, setRevAmount] = useState("0");
  const [frequency, setFrequency] = useState("one_time");
  const [revenueDate, setRevenueDate] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // Inline editing
  const [editingId, setEditingId] = useState(null);
  const [editAmount, setEditAmount] = useState("");
  const [editRevenueDate, setEditRevenueDate] = useState("");
  const [editStartDate, setEditStartDate] = useState("");
  const [editEndDate, setEditEndDate] = useState("");
  const [editFrequency, setEditFrequency] = useState("");

  // Se è mobile
  const isMobile = useMediaQuery({ maxWidth: 768 });

  useEffect(() => {
    if (restaurantId) {
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restaurantId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const data = await fetchExtraRevenues({ restaurant_id: restaurantId });
      setRevenues(data);
    } catch (err) {
      console.error("Errore fetchExtraRevenues:", err);
    } finally {
      setLoading(false);
    }
  };

  // Creazione nuovo ricavo
  const handleCreate = async (e) => {
    e.preventDefault();
    if (!restaurantId) return;

    // Logica date obbligatorie per ricavi ricorrenti
    if (frequency !== "one_time") {
      if (!startDate) {
        if (frequency === "daily") {
          const today = new Date().toISOString().substring(0, 10);
          setStartDate(today);
        } else {
          alert("La data di inizio è obbligatoria per i ricavi ricorrenti!");
          return;
        }
      }
    }

    try {
      const payload = {
        revenue_name: revName,
        amount: parseFloat(revAmount),
        frequency,
        restaurant_id: restaurantId,
      };

      if (frequency === "one_time") {
        if (revenueDate) payload.revenue_date = revenueDate;
      } else {
        payload.start_date = startDate || new Date().toISOString().substring(0, 10);
        if (endDate) payload.end_date = endDate;
      }

      await createExtraRevenue(payload);

      // Reset
      setRevName("");
      setRevAmount("0");
      setFrequency("one_time");
      setRevenueDate("");
      setStartDate("");
      setEndDate("");

      loadData();
    } catch (err) {
      console.error("Errore createExtraRevenue:", err);
    }
  };

  // Inizia la modifica inline
  const handleStartEdit = (r) => {
    setEditingId(r.id);
    setEditAmount(String(r.amount));
    setEditFrequency(r.frequency);

    if (r.frequency === "one_time") {
      setEditRevenueDate(r.revenue_date ? r.revenue_date.substring(0, 10) : "");
      setEditStartDate("");
      setEditEndDate("");
    } else {
      setEditRevenueDate("");
      setEditStartDate(r.start_date ? r.start_date.substring(0, 10) : "");
      setEditEndDate(r.end_date ? r.end_date.substring(0, 10) : "");
    }
  };

  // Salva modifiche
  const handleSaveEdit = async (id) => {
    try {
      const payload = {};

      if (editAmount !== "") {
        payload.amount = parseFloat(editAmount);
      }

      if (editFrequency === "one_time") {
        payload.revenue_date = editRevenueDate || null;
      } else {
        if (!editStartDate) {
          if (editFrequency === "daily") {
            const today = new Date().toISOString().substring(0, 10);
            payload.start_date = today;
          } else {
            alert("La data di inizio è obbligatoria per i ricavi ricorrenti!");
            return;
          }
        } else {
          payload.start_date = editStartDate;
        }
        payload.end_date = editEndDate || null;
      }

      await updateExtraRevenue(id, payload);

      handleCancelEdit();
      loadData();
    } catch (err) {
      console.error("Errore updateExtraRevenue:", err);
    }
  };

  // Annulla modifica
  const handleCancelEdit = () => {
    setEditingId(null);
    setEditAmount("");
    setEditRevenueDate("");
    setEditStartDate("");
    setEditEndDate("");
    setEditFrequency("");
  };

  // Elimina ricavo
  const handleDelete = async (id) => {
    if (!window.confirm("Eliminare il ricavo extra?")) return;
    try {
      await deleteExtraRevenue(id);
      loadData();
    } catch (err) {
      console.error("Errore deleteExtraRevenue:", err);
    }
  };

  // Se ristorante non selezionato
  if (!restaurantId) {
    return (
      <div style={{ padding: "16px", fontSize: "16px" }}>
        Seleziona un ristorante per vedere i ricavi extra.
      </div>
    );
  }

  // Se stiamo caricando
  if (loading) {
    return (
      <div style={{ padding: "16px", fontSize: "16px" }}>
        Caricamento ricavi extra...
      </div>
    );
  }

  return (
    <div style={styles.container}>
      

      {/* FORM CREAZIONE */}
      <div style={styles.formCard}>
       
        <form onSubmit={handleCreate}>
          <div style={styles.formRow}>
            <label style={styles.label}>Nome Ricavo</label>
            <input
              style={styles.input}
              type="text"
              value={revName}
              onChange={(e) => setRevName(e.target.value)}
              required
            />
          </div>
          <div style={styles.formRow}>
            <label style={styles.label}>Importo</label>
            <input
              style={styles.input}
              type="number"
              step="0.01"
              value={revAmount}
              onChange={(e) => setRevAmount(e.target.value)}
              required
            />
          </div>
          <div style={styles.formRow}>
            <label style={styles.label}>Frequenza</label>
            <select
              style={styles.select}
              value={frequency}
              onChange={(e) => setFrequency(e.target.value)}
            >
              <option value="one_time">One-Time</option>
              <option value="daily">Daily</option>
              <option value="monthly">Monthly</option>
              <option value="yearly">Yearly</option>
            </select>
          </div>

          {frequency === "one_time" ? (
            <div style={styles.formRow}>
              <label style={styles.label}>Data Ricavo (opzionale)</label>
              <input
                style={styles.input}
                type="date"
                value={revenueDate}
                onChange={(e) => setRevenueDate(e.target.value)}
              />
            </div>
          ) : (
            <>
              <div style={styles.formRow}>
                <label style={styles.label}>Data Inizio *</label>
                <input
                  style={styles.input}
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </div>
              <div style={styles.formRow}>
                <label style={styles.label}>Data Fine (opzionale)</label>
                <input
                  style={styles.input}
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </div>
            </>
          )}

          <button style={styles.addButton} type="submit">
            Aggiungi Ricavo
          </button>
        </form>
      </div>

      {/* ELENCO RICAVI */}
      {revenues.length === 0 ? (
        <div style={{ fontSize: "16px" }}>Nessun ricavo extra presente.</div>
      ) : isMobile ? (
        // CARD LAYOUT mobile
        revenues.map((r) => {
          const editing = editingId === r.id;
          return (
            <div key={r.id} style={styles.card}>
              <h4 style={{ marginTop: 0 }}>{r.revenue_name}</h4>
              {!editing ? (
                <>
                  {r.frequency === "one_time" ? (
                    <div>
                      <span style={styles.mobileFieldLabel}>Frequenza:</span>{" "}
                      {r.frequency}
                      <div>
                        <span style={styles.mobileFieldLabel}>Data:</span>{" "}
                        {r.revenue_date ? r.revenue_date.substring(0, 10) : "N/D"}
                      </div>
                    </div>
                  ) : (
                    <>
                      <div>
                        <span style={styles.mobileFieldLabel}>Frequenza:</span>{" "}
                        {r.frequency}
                      </div>
                      <div>
                        <span style={styles.mobileFieldLabel}>Data da:</span>{" "}
                        {r.start_date ? r.start_date.substring(0, 10) : "N/D"}
                      </div>
                      <div>
                        <span style={styles.mobileFieldLabel}>Data a:</span>{" "}
                        {r.end_date ? r.end_date.substring(0, 10) : "N/D"}
                      </div>
                    </>
                  )}
                  <div>
                    <span style={styles.mobileFieldLabel}>Importo:</span> {r.amount}
                  </div>
                  <div style={{ marginTop: "8px" }}>
                    <button style={styles.actionButton} onClick={() => handleStartEdit(r)}>
                      Modifica
                    </button>
                    <button
                      style={{ ...styles.actionButton, ...styles.cancelButton }}
                      onClick={() => handleDelete(r.id)}
                    >
                      Elimina
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <span style={styles.mobileFieldLabel}>Importo:</span>
                  <input
                    style={styles.editInput}
                    type="number"
                    step="0.01"
                    value={editAmount}
                    onChange={(e) => setEditAmount(e.target.value)}
                  />

                  {editFrequency === "one_time" ? (
                    <>
                      <span style={styles.mobileFieldLabel}>Data Ricavo:</span>
                      <input
                        style={styles.editInput}
                        type="date"
                        value={editRevenueDate}
                        onChange={(e) => setEditRevenueDate(e.target.value)}
                      />
                    </>
                  ) : (
                    <>
                      <span style={styles.mobileFieldLabel}>Data Inizio:</span>
                      <input
                        style={styles.editInput}
                        type="date"
                        value={editStartDate}
                        onChange={(e) => setEditStartDate(e.target.value)}
                      />
                      <span style={styles.mobileFieldLabel}>Data Fine:</span>
                      <input
                        style={styles.editInput}
                        type="date"
                        value={editEndDate}
                        onChange={(e) => setEditEndDate(e.target.value)}
                      />
                    </>
                  )}

                  <button style={styles.actionButton} onClick={() => handleSaveEdit(r.id)}>
                    Salva
                  </button>
                  <button
                    style={{ ...styles.actionButton, ...styles.cancelButton }}
                    onClick={handleCancelEdit}
                  >
                    Annulla
                  </button>
                </>
              )}
            </div>
          );
        })
      ) : (
        // TABELLA desktop
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Nome</th>
              <th style={styles.th}>Frequenza</th>
              <th style={styles.th}>Data</th>
              <th style={styles.th}>Data da</th>
              <th style={styles.th}>Data a</th>
              <th style={styles.th}>Importo</th>
              <th style={styles.th}>Azioni</th>
            </tr>
          </thead>
          <tbody>
            {revenues.map((r) => {
              const editing = editingId === r.id;
              return (
                <tr key={r.id}>
                  <td style={styles.td}>{r.revenue_name}</td>
                  <td style={styles.td}>{r.frequency}</td>
                  <td style={styles.td}>
                    {editing && r.frequency === "one_time" ? (
                      <input
                        style={styles.editInput}
                        type="date"
                        value={editRevenueDate}
                        onChange={(e) => setEditRevenueDate(e.target.value)}
                      />
                    ) : r.frequency === "one_time" && r.revenue_date ? (
                      r.revenue_date.substring(0, 10)
                    ) : (
                      ""
                    )}
                  </td>
                  <td style={styles.td}>
                    {editing && r.frequency !== "one_time" ? (
                      <input
                        style={styles.editInput}
                        type="date"
                        value={editStartDate}
                        onChange={(e) => setEditStartDate(e.target.value)}
                      />
                    ) : r.frequency !== "one_time" && r.start_date ? (
                      r.start_date.substring(0, 10)
                    ) : (
                      ""
                    )}
                  </td>
                  <td style={styles.td}>
                    {editing && r.frequency !== "one_time" ? (
                      <input
                        style={styles.editInput}
                        type="date"
                        value={editEndDate}
                        onChange={(e) => setEditEndDate(e.target.value)}
                      />
                    ) : r.frequency !== "one_time" && r.end_date ? (
                      r.end_date.substring(0, 10)
                    ) : (
                      ""
                    )}
                  </td>
                  <td style={styles.td}>
                    {editing ? (
                      <input
                        style={styles.editInput}
                        type="number"
                        step="0.01"
                        value={editAmount}
                        onChange={(e) => setEditAmount(e.target.value)}
                      />
                    ) : (
                      r.amount
                    )}
                  </td>
                  <td style={styles.td}>
                    {editing ? (
                      <>
                        <button
                          style={styles.actionButton}
                          onClick={() => handleSaveEdit(r.id)}
                        >
                          Salva
                        </button>
                        <button
                          style={{ ...styles.actionButton, ...styles.cancelButton }}
                          onClick={handleCancelEdit}
                        >
                          Annulla
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          style={styles.actionButton}
                          onClick={() => handleStartEdit(r)}
                        >
                          Modifica
                        </button>
                        <button
                          style={{
                            ...styles.actionButton,
                            ...styles.cancelButton,
                            marginLeft: 8,
                          }}
                          onClick={() => handleDelete(r.id)}
                        >
                          Elimina
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}
